/**
 * Sección "Start Next 7 Days": quién entra en los próximos 7 días, por
 * clapbase, y su pipeline. Antes era una card con popup dentro de Proceso.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { AlertCircle, MapPin } from 'lucide-react';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { AltasTable } from './AltasTable';
import { usePeopleFlowRows } from './usePeopleFlowRows';

export function OnboardingUpcomingTab({ lastRunAt }: { lastRunAt: string | null }) {
  const { t } = useTranslation();
  const { rows, buckets, isLoading, error } = usePeopleFlowRows();
  const byClapbase = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of buckets.upcoming) {
      const key = r.row.alta.comunidad || '—';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'es'));
  }, [buckets.upcoming]);

  if (isLoading) return <WinclapLoader />;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('users.people_panel.load_error')}</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {byClapbase.length > 0 && (
        <Card className="flex flex-wrap items-center gap-2 p-3">
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" />{t('users.people_panel.upcoming.by_clapbase', 'Por clapbase:')}</span>
          {byClapbase.map(([clapbase, n]) => (
            <span key={clapbase} className="rounded-full bg-muted px-2.5 py-0.5 text-sm">
              {clapbase} <span className="font-semibold tabular-nums">{n}</span>
            </span>
          ))}
        </Card>
      )}
      <AltasTable rows={buckets.upcoming} allRows={rows} emptyText={t('users.people_panel.upcoming.empty', 'Nadie entra en los próximos 7 días.')} lastRunAt={lastRunAt} />
    </div>
  );
}
